import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { LgConsole } from "@logex/framework/core";
import {
    BrowserCacheProvider,
    IAppSession,
    LG_APP_SESSION,
    ServerDefinitionsBase
} from "@logex/framework/lg-application";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { StringKeyOf } from "@logex/framework/types";
import { Dictionary } from "lodash";
import { DefinitionsGateway } from "../gateways/definitions.gateway";
import {
    AppDefinitionEntry,
    DefinitionsAddressType,
    DefinitionsContactType,
    DefinitionsCountry,
    DefinitionsHealthcareServiceProviderCategory,
    DefinitionsHealthcareServiceSupplierCategory,
    DefinitionsHierarchyType,
    DefinitionsNumberOfBeds,
    DefinitionsOrganizationNameTag,
    DefinitionsOrganizationType,
    DefinitionsRegulationCode,
    IAppDefinitions
} from "../types/app-definitions";

export type DefinitionKey = StringKeyOf<IAppDefinitions>;

@Injectable({
    providedIn: "root"
})
export class AppDefinitions
    extends ServerDefinitionsBase<IAppDefinitions>
    implements IAppDefinitions
{
    // ----------------------------------------------------------------------------------
    // Fields
    addressType: Dictionary<DefinitionsAddressType> = this.generate<
        DefinitionsAddressType,
        Dictionary<DefinitionsAddressType>
    >({
        generator: () => this._definitionsGateway.getAddressType()
    });

    contactType: Dictionary<DefinitionsContactType> = this.generate<
        DefinitionsContactType,
        Dictionary<DefinitionsContactType>
    >({ generator: () => this._definitionsGateway.getContactType() });

    country: Dictionary<DefinitionsCountry> = this.generate<
        DefinitionsCountry,
        Dictionary<DefinitionsCountry>
    >({
        generator: () => this._definitionsGateway.getCountry()
    });

    healthcareServiceProviderCategory: Dictionary<DefinitionsHealthcareServiceProviderCategory> =
        this.generate<
            DefinitionsHealthcareServiceProviderCategory,
            Dictionary<DefinitionsHealthcareServiceProviderCategory>
        >({ generator: () => this._definitionsGateway.getHealthcareServiceProviderCategory() });

    healthcareServiceSupplierCategory: Dictionary<DefinitionsHealthcareServiceSupplierCategory> =
        this.generate<
            DefinitionsHealthcareServiceSupplierCategory,
            Dictionary<DefinitionsHealthcareServiceSupplierCategory>
        >({ generator: () => this._definitionsGateway.getHealthcareServiceSupplierCategory() });

    organizationType: Dictionary<DefinitionsOrganizationType> = this.generate<
        DefinitionsOrganizationType,
        Dictionary<DefinitionsOrganizationType>
    >({
        generator: () => this._definitionsGateway.getOrganizationType()
    });

    organizationNameTag: Dictionary<DefinitionsOrganizationNameTag> = this.generate<
        DefinitionsOrganizationNameTag,
        Dictionary<DefinitionsOrganizationNameTag>
    >({
        generator: () => this._definitionsGateway.getOrganizationNameTag()
    });

    hierarchyType: Dictionary<DefinitionsHierarchyType> = this.generate<
        DefinitionsHierarchyType,
        Dictionary<DefinitionsHierarchyType>
    >({
        generator: () => this._definitionsGateway.getOrganizationHierarchyType()
    });

    regulationCode: Dictionary<DefinitionsRegulationCode> = this.generate<
        DefinitionsRegulationCode,
        Dictionary<DefinitionsHierarchyType>
    >({
        generator: () => this._definitionsGateway.getRegulationCode()
    });

    numberOfBeds: Dictionary<DefinitionsNumberOfBeds> = this.generate<
        DefinitionsNumberOfBeds,
        Dictionary<DefinitionsNumberOfBeds>
    >({
        generator: () => this._definitionsGateway.getNumberOfBeds()
    });

    constructor(
        _translate: LgTranslateService,
        _httpClient: HttpClient,
        _cacheProvider: BrowserCacheProvider,
        _console: LgConsole,
        @Inject(LG_APP_SESSION) _appSession: IAppSession,
        private _definitionsGateway: DefinitionsGateway
    ) {
        super();
        this.init();
    }

    getDisplayName(sectionName: DefinitionKey, code: any): string {
        const entry = this.getEntry(sectionName, code);

        return entry ? entry.displayName : "";
    }

    getSortedSectionData<
        T extends AppDefinitionEntry | DefinitionsHealthcareServiceProviderCategory
    >(sectionName: DefinitionKey): T[] {
        return Object.values(this.getSection(sectionName).data).sort(
            (a: AppDefinitionEntry, b: AppDefinitionEntry) => a.sortOrder - b.sortOrder
        );
    }
}
